/**
 * Shared helpers for Supperkeep's branded transactional auth emails.
 *
 * Dependency-free on purpose: the server uses the validation and link helpers, the client uses the
 * request helpers. Nothing here touches the DOM, Firebase, Node APIs or any secret.
 */

/** Server routes that send auth email. Mounted under `/api` (outside the `/api/v1` group). */
export const AUTH_EMAIL_ROUTES = {
  /** Anonymous. Always answers the same way whether or not the address has an account. */
  reset: "/api/auth/email/reset",
  /** Authenticated (an unverified Firebase token is accepted). Sends to the token's own address. */
  verify: "/api/auth/email/verify",
} as const;

/** The only action modes Supperkeep ever puts in an email link. */
export const AUTH_ACTION_MODES = ["verifyEmail", "resetPassword"] as const;
export type AuthActionMode = (typeof AUTH_ACTION_MODES)[number];
/** In-app handler route (`src/client/routes/auth/AuthAction.tsx`). */
export const AUTH_ACTION_PATH = "/auth/action";

export const MAX_EMAIL_LENGTH = 254;

/**
 * Deliberately stricter than RFC 5322: no quoted local parts, no comments, no angle brackets,
 * commas, semicolons or whitespace — the characters that make address-header injection possible.
 */
const EMAIL_RE = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]{1,64}@[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)+$/;

export const normalizeEmail = (raw: string): string => raw.trim().toLowerCase();

/** True for an address that is safe to place in a `To:` header verbatim. */
export function isEmailAddress(value: string): boolean {
  if (!value || value.length > MAX_EMAIL_LENGTH) return false;
  if (hasControlCharacter(value) || value.includes("..")) return false;
  const local = value.slice(0, value.indexOf("@"));
  if (local.startsWith(".") || local.endsWith(".")) return false;
  return EMAIL_RE.test(value);
}

/** CR, LF and other control characters are how header injection is smuggled in. */
export const hasControlCharacter = (value: string): boolean => /[\u0000-\u001f\u007f]/.test(value);

/** Collapse anything that could break out of a header into a single space. */
export const toHeaderValue = (value: string): string => value.replace(/[\u0000-\u001f\u007f]+/g, " ").trim();

export const isAuthActionMode = (value: string): value is AuthActionMode => (AUTH_ACTION_MODES as readonly string[]).includes(value);

/**
 * Build the in-app action link from a whitelisted mode and the one-time code. The caller never
 * influences the destination: only `mode` and `oobCode` are carried over, and `mode` must be one of
 * the two constants above.
 */
export function authActionUrl(publicBaseUrl: string, mode: AuthActionMode, oobCode: string): string {
  const base = publicBaseUrl.replace(/\/+$/, "");
  return `${base}${AUTH_ACTION_PATH}?mode=${mode}&oobCode=${encodeURIComponent(oobCode)}`;
}

// ---------- client helpers ----------

export interface ResetEmailResponse {
  /** Accepted for processing. Never a delivery receipt, and never account existence. */
  status: "accepted";
}
export interface VerificationEmailResponse {
  status: "sent" | "already_verified";
}

export class AuthEmailError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "AuthEmailError";
  }
}

/**
 * Which path sends auth email on this deployment. With the Firebase Auth emulator (local
 * development) Firebase's own client SDK keeps working and the codes land in the dev outbox; a real
 * deployment uses the server endpoints so the message is Supperkeep-branded.
 */
export const usesServerEmail = (config: { authEmulatorUrl: string | null }): boolean => !config.authEmulatorUrl;

async function postJson<T>(path: string, body: unknown, token?: string | null): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json", Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  let res: Response;
  try {
    res = await fetch(path, { method: "POST", headers, body: JSON.stringify(body), cache: "no-store", signal: AbortSignal.timeout(30_000) });
  } catch {
    throw new AuthEmailError(0, "offline", "Couldn't reach Supperkeep. Check your connection and try again.");
  }
  const payload = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
  if (!res.ok) {
    throw new AuthEmailError(res.status, payload?.error?.code ?? "internal", payload?.error?.message ?? "Couldn't send that email. Try again in a moment.");
  }
  return payload as T;
}

/** Ask for a password-reset email. Resolves the same way for unknown and known addresses. */
export const requestPasswordResetEmail = (email: string): Promise<ResetEmailResponse> =>
  postJson<ResetEmailResponse>(AUTH_EMAIL_ROUTES.reset, { email: normalizeEmail(email) });

/** Ask for a verification email for the signed-in account. The recipient comes from the token. */
export const requestVerificationEmail = (token: string): Promise<VerificationEmailResponse> =>
  postJson<VerificationEmailResponse>(AUTH_EMAIL_ROUTES.verify, {}, token);
